import React, { useEffect, useState } from 'react';
import {
  Button,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
  Grid,
  Alert,
  Container,
  LinearProgress,
  styled,
  linearProgressClasses,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import DOMPurify from 'dompurify';
import COLORS from 'theme/colors';

import { FormProvider, useFieldArray, useForm } from 'react-hook-form';

interface DynamicTableRendererProps {
  question: any;
  handleNext: (payload: any) => void;
  openModal: () => void;
}

type TableColumn = {
  id: string;
  name: string;
  fieldId?: string;
  ref?: string;
  type: 'text' | 'number' | 'dropdown';
  options?: string[];
};

type TableRowData = {
  rowId: number;
  values: Record<string, any>;
};

const DynamicTableRenderer: React.FC<DynamicTableRendererProps> = ({ question, handleNext, openModal }) => {
  const methods = useForm<{ rows: TableRowData[] }>({
    defaultValues: {
      rows: [],
    },
  });
  const [cellErrors, setCellErrors] = useState<Record<string, string>>({});
  const { control, watch, setValue, handleSubmit } = methods;
  const tableColumns: TableColumn[] = question?.tableColumns ?? [];
  const [submitError, setSubmitError] = useState(false);
  const { replace } = useFieldArray({
    control,
    name: 'rows',
  });
  const rows = watch('rows') ?? [];
  const fixedRowCount = question?.maxRows ?? 5;
  const createEmptyRow = (columns: TableColumn[], rowId: number): TableRowData => ({
    rowId,
    values: columns.reduce(
      (acc, column) => {
        acc[column.id] = '';
        return acc;
      },
      {} as Record<string, string>
    ),
  });

  useEffect(() => {
    if (question.answerMode === 'fixed_table') {
      replace(Array.from({ length: fixedRowCount }, (_, index) => createEmptyRow(tableColumns, index + 1)));
    } else {
      replace([createEmptyRow(tableColumns, 1)]);
    }
  }, [question]);

  const handleCellChange = (rowIndex: number, columnId: string, value: string) => {
    const key = `${rowIndex}-${columnId}`;

    setValue(`rows.${rowIndex}.values.${columnId}` as any, value);

    if (value.length > 200) {
      setCellErrors((prev) => ({
        ...prev,
        [key]: 'Maximum 200 characters allowed.',
      }));
    } else {
      setCellErrors((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    }
  };

  const hasNonDropdownValue = (row: TableRowData) => {
    return tableColumns.some((column) => {
      if (column.type === 'dropdown') {
        return false;
      }
      const value = row.values?.[column.id];
      return value && value.toString().trim() !== '';
    });
  };
  const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 8,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      backgroundColor: theme.palette.mode === 'light' ? '#3467ff' : '#308fe8',
    },
  }));

  const handleBackButton = () => {
    handleNext({
      event: 'back',
    });
  };

  const renderFixedTable = () => {
    return (
      <>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: '0px',
            overflow: 'hidden',
          }}
        >
          <Table
            size='small'
            sx={{
              borderCollapse: 'collapse',
              '& .MuiTableCell-root': {
                padding: '0px',
                textAlign: 'center',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    height: 42,
                    width: 50,
                    minWidth: 50,
                    background: '#FFC44D',
                    textAlign: 'center',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#1C1F24',
                    borderRadius: '4px 0 0 4px',
                  }}
                >
                  No.
                </TableCell>

                {tableColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      border: '1px solid #f7f6f3',
                      minWidth: column.type === 'dropdown' ? 100 : 180,
                      background: '#FFC44D',
                      fontFamily: 'Graphik, sans-serif',
                      fontSize: '14px',
                      color: '#1C1F24',
                      fontWeight: 400,
                      lineHeight: '12px',
                      align: 'center',
                    }}
                  >
                    {column.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>{rows.map(renderRow)}</TableBody>
          </Table>
        </TableContainer>
        {Object.keys(cellErrors).length > 0 && (
          <Box mt={2}>
            <Typography
              sx={{
                display: 'inline-block',
                backgroundColor: '#D92D20',
                color: '#FFFFFF',
                px: 2,
                py: 1,
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Maximum 200 characters allowed per cell.
            </Typography>
          </Box>
        )}
      </>
    );
  };
  const renderTable = (question: any) => {
    if (question?.answerMode) {
      if (question.answerMode === 'fixed_table') {
        return renderFixedTable();
      }

      if (question.answerMode === 'vertical_fixed_table') {
        return renderVerticalFixedTable(question.tableColumns);
      }
    }
    return null;
  };

  const renderVerticalFixedTable = (columns: TableColumn[]) => (
    <TableContainer
      component={Paper}
      sx={{
        overflow: 'hidden',
        boxShadow: 'none',
      }}
    >
      <Table
        size='small'
        sx={{
          borderCollapse: 'collapse',
          '& .MuiTableCell-root': {
            padding: '0px',
          },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              padding: '8px',
              height: 42,
            }}
          >
            <TableCell />
            <TableCell
              align='center'
              sx={{
                height: 42,
                width: '35%',
                background: '#FFC44D',
                textAlign: 'center',
                fontWeight: 500,
                fontSize: '14px',
                color: '#1C1F24',
                borderRadius: '4px 0 0 4px',
              }}
            >
              Details
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {columns.map((column) => (
            <TableRow key={column.id}>
              <TableCell
                sx={{
                  padding: '0px 20px !important',
                  height: 42,
                  background: '#FFC44D',
                  textAlign: 'left',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#1C1F24',
                  borderRadius: '4px 0 0 4px',
                }}
              >
                {column.name}
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  value={rows?.[0]?.values?.[column.id] ?? ''}
                  onChange={(e) => handleCellChange(0, column.id, e.target.value)}
                  error={!!cellErrors[`0-${column.id}`]}
                  inputProps={{
                    maxLength: 201,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0px !important',
                    },

                    '& .MuiOutlinedInput-notchedOutline': {
                      borderRadius: '0px !important',
                    },

                    '& fieldset': {
                      borderRadius: '0px !important',
                    },
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const onSubmit = (data: any) => {
    if (Object.keys(cellErrors).length > 0) {
      return;
    }
    if (question.answerMode === 'fixed_table') {
      const populatedRows = data.rows.filter((row: TableRowData) => hasNonDropdownValue(row));
      if (populatedRows.length === 0) {
        setSubmitError(true);
        return;
      }
    }

    const firstRow = data.rows?.[0]?.values ?? {};
    const tableRows = tableColumns.map((column) => ({
      column: column.id,
      name: column.name,
      value: firstRow[column.id] ?? '',
    }));
    if (
      question.answerMode === 'vertical_fixed_table' &&
      question.type === 'mandatory' &&
      !tableRows.some((row) => row.value && row.value.toString().trim() !== '')
    ) {
      setSubmitError(true);
      return;
    }

    const populatedRows = data.rows.filter((row: TableRowData) =>
      Object.values(row.values ?? {}).some((value) => value && value.toString().trim() !== '')
    );

    const mappedAnswers = populatedRows.flatMap((row: TableRowData, rowIndex: number) =>
      tableColumns.map((column, columnIndex) => ({
        fieldId: question.mapping?.[rowIndex]?.[columnIndex],
        value: row.values?.[column.id] ?? '',
      }))
    );

    const payload = {
      quizId: question.questionId,
      answerType: question.answerMode,
      answers: {
        columns: tableColumns.map((column) => ({
          id: column.id,
          name: column.name,
          fieldId: column?.fieldId,
        })),
        rows: data.rows.map(buildRowData),
        mappedAnswers,
      },
    };
    console.log(payload);
    handleNext(payload);
  };

  const renderDropdownCell = (row: TableRowData, rowIndex: number, column: TableColumn) => (
    <TextField
      select
      fullWidth
      size='small'
      value={row.values?.[column.id] ?? ''}
      onChange={(e) => setValue(`rows.${rowIndex}.values.${column.id}` as any, e.target.value)}
    >
      {column.options?.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </TextField>
  );

  const renderTextCell = (row: TableRowData, rowIndex: number, column: TableColumn) => (
    <TextField
      fullWidth
      size='small'
      type={column.type === 'number' ? 'number' : 'text'}
      value={row.values?.[column.id] ?? ''}
      onChange={(e) => {
        handleCellChange(rowIndex, column.id, e.target.value);

        if (submitError) {
          setSubmitError(false);
        }
      }}
      error={!!cellErrors[`${rowIndex}-${column.id}`]}
      inputProps={{ maxLength: 201 }}
    />
  );

  const renderCell = (row: TableRowData, rowIndex: number, column: TableColumn) => (
    <TableCell key={column.id}>
      {column.type === 'dropdown' ? renderDropdownCell(row, rowIndex, column) : renderTextCell(row, rowIndex, column)}
    </TableCell>
  );

  const buildRowData = (row: TableRowData): Record<string, string> => {
    return tableColumns.reduce(
      (acc, column) => ({
        ...acc,
        [column.id]: row.values?.[column.id] ?? '',
      }),
      {}
    );
  };
  const renderRow = (row: TableRowData, rowIndex: number) => (
    <TableRow key={row.rowId}>
      <TableCell>{rowIndex + 1}</TableCell>

      {tableColumns.map((column) => renderCell(row, rowIndex, column))}
    </TableRow>
  );
  return (
    <FormProvider {...methods}>
      <Grid container maxWidth='100%'>
        <Box
          sx={{
            position: {
              xs: 'inherit',
              md: 'absolute',
            },
            left: '20px',
            top: '130px',
          }}
        >
          <Button
            sx={{
              background: COLORS.LightBlue,
              minWidth: 140,
              mb: 1,
            }}
            variant='outlined'
            startIcon={<ArrowBackIcon />}
            onClick={handleBackButton}
            disabled={question?.event === 'back'}
          >
            Back
          </Button>
        </Box>

        <Grid item xs={12}>
          <BorderLinearProgress value={question?.progress} variant='determinate' />
          {question?.header !== null ? question.header : null}
          <Container sx={{ background: '#fff', padding: '20px 40px', borderRadius: '12px' }}>
            <Typography sx={{ color: '#6941C6', fontSize: '18px', fontWeight: 500, mb: '24px', lineHeight: '28px' }}>
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question?.heading) }} />
            </Typography>

            <Typography
              mb={3}
              sx={{
                lineHeight: '28px',
                mt: '5px',
                mb: '24px',
                fontSize: '18px',
                fontWeight: 500,
                color: '#2C3446',
                fontStyle: 'italic',
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question?.prompt) }} />{' '}
            </Typography>

            <Typography
              mb={3}
              sx={{ lineHeight: '30px', mt: '5px', mb: '6px', fontSize: '20px', fontWeight: 500, color: '#2C3446' }}
            >
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question?.question) }} />{' '}
            </Typography>

            {question?.informationSection && question?.informationSection !== null ? (
              <Box mb={6}>
                <Alert severity='info' sx={{ color: '#1D2939', fontSize: '14px', fontWeight: 400 }}>
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question?.informationSection) }} />{' '}
                </Alert>
              </Box>
            ) : null}

            {question?.warn && question?.warn !== null ? (
              <Box>
                <Grid
                  container
                  mt={4}
                  sx={{
                    background: '#F8F5FF',
                    borderRadius: '8px',
                    display: 'flex',
                    padding: '20px 12px',
                    alignItems: 'flex-start',
                    alignSelf: 'stretch',
                  }}
                >
                  <Grid item xs={0.5}>
                    <WarningAmberIcon color='error' />
                  </Grid>
                  <Grid item xs={11.5}>
                    <Typography>
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question?.warn) }} />
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ) : null}

            {question.info !== null ? (
              <Box sx={{ background: '#F8F5FF', p: '8px 12px', alignSelf: 'stretch', borderRadius: '8px', mb: '48px' }}>
                <Grid container>
                  <Grid item xs={0.5}>
                    <InfoOutlinedIcon fontSize='small' color='primary' sx={{ mr: '10px' }} />
                  </Grid>
                  <Grid item xs={11.5} mt='2px'>
                    <Typography color='#1C1F24'>
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question?.info) }} />
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ) : null}

            {question?.instruction !== null ? (
              <Typography mb={3} sx={{ fontWeight: 400, fontSize: '18px', color: '#2C3446' }}>
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question?.instruction) }} />
              </Typography>
            ) : null}

            {renderTable(question)}
            {submitError && (
              <Box mt={2}>
                <Typography
                  sx={{
                    display: 'inline-block',
                    backgroundColor: '#D92D20',
                    color: '#FFFFFF',
                    px: 2,
                    py: 1,
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                  data-testid='submit-error'
                >
                  At least 1 field is required.
                </Typography>
              </Box>
            )}

            <Box mt={3} display='flex' gap={2} justifyContent='center'>
              <Button
                variant='contained'
                disabled={Object.keys(cellErrors).length > 0}
                onClick={handleSubmit(onSubmit)}
              >
                Next
              </Button>

              <Button variant='outlined' onClick={openModal}>
                Continue Later
              </Button>
            </Box>
          </Container>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default DynamicTableRenderer;
